import { Link, useParams } from "react-router-dom";
import JsonLd from "../../components/JsonLd/JsonLd";
import Layout from "../../components/Layout/Layout";
import { festById } from "../../data/fests";
import { kindLabel, overnightLabel, TAG_LABELS } from "../../lib/access";
import { formatCoords } from "../../lib/geo";
import { cadenceLabel, formatRange, nextSeason } from "../../lib/season";
import { SITE_ORIGIN } from "../../lib/site";

const FestDetail = () => {
  const { id } = useParams();
  const fest = id ? festById(id) : undefined;
  const season = fest ? nextSeason(fest) : null;

  return (
    <Layout title={fest ? `${fest.name} | LotCamp` : "Fest | LotCamp"}>
      <section className="max-w-3xl mx-auto px-6 py-12 w-full">
        <Link to="/fests" className="text-turquoise hover:text-mars text-sm">
          ← Back to fests
        </Link>

        {!fest && <p className="mt-8 text-lg">That fest is not in the v1 list yet.</p>}

        {fest && (
          <article className="mt-6">
            <JsonLd
              data={{
                "@context": "https://schema.org",
                "@type": "Festival",
                name: fest.name,
                url: `${SITE_ORIGIN}/fests/${fest.id}`,
                description: fest.whyGo,
                startDate: season?.start,
                endDate: season?.end,
                location: {
                  "@type": "Place",
                  name: fest.venue,
                  address: {
                    "@type": "PostalAddress",
                    addressLocality: fest.city,
                    addressRegion: fest.state,
                    addressCountry: fest.country ?? "US",
                  },
                  geo: {
                    "@type": "GeoCoordinates",
                    latitude: fest.latitude,
                    longitude: fest.longitude,
                  },
                },
              }}
            />
            <p className="text-sun font-semibold tracking-wide uppercase text-sm">
              {fest.region} · {fest.city}, {fest.state}
            </p>
            <h1 className="text-4xl font-bold text-alt-blk mt-2">{fest.name}</h1>
            <p className="text-xl text-alt-blk/70 mt-2">{kindLabel(fest)}</p>
            {season && (
              <p className="text-sm text-alt-blk/50 mt-1">
                {formatRange(season.start, season.end)} · {cadenceLabel(season.cadence)}
              </p>
            )}

            <dl className="grid sm:grid-cols-2 gap-4 mt-8 text-sm">
              <div className="rounded-2xl bg-white p-4 border border-alt-blk/10">
                <dt className="text-alt-blk/50">Overnight</dt>
                <dd className="mt-1 font-medium">{overnightLabel(fest.overnight)}</dd>
              </div>
              <div className="rounded-2xl bg-white p-4 border border-alt-blk/10">
                <dt className="text-alt-blk/50">Coordinates</dt>
                <dd className="mt-1 font-medium">
                  {formatCoords(fest.latitude, fest.longitude)}
                </dd>
              </div>
            </dl>

            <div className="flex flex-wrap gap-1.5 mt-6">
              {fest.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[11px] uppercase tracking-wide px-2.5 py-1 rounded-full border border-alt-blk/15"
                >
                  {TAG_LABELS[tag]}
                </span>
              ))}
            </div>

            <h2 className="text-2xl font-bold mt-10 mb-3">Why it is on the list</h2>
            <p className="leading-relaxed">{fest.whyGo}</p>

            <h2 className="text-2xl font-bold mt-10 mb-3">Camping and overnight</h2>
            <p className="leading-relaxed">{fest.campingNotes}</p>

            <h2 className="text-2xl font-bold mt-10 mb-3">Trip notes</h2>
            <p className="leading-relaxed">{fest.tripNotes}</p>

            <h2 className="text-2xl font-bold mt-10 mb-3">Etiquette</h2>
            <p className="leading-relaxed">{fest.etiquette}</p>

            <p className="text-sm text-alt-blk/60 mt-8">
              Venue: {fest.venue}. Typical season: {fest.typical.notes}
            </p>

            <div className="flex flex-wrap gap-3 mt-10">
              <Link
                to={`/?fest=${fest.id}`}
                className="inline-block bg-mars hover:bg-sunset text-white px-8 py-4 rounded-2xl font-semibold"
              >
                Show on map
              </Link>
              {fest.officialUrl && (
                <a
                  href={fest.officialUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block border border-alt-blk px-8 py-4 rounded-2xl font-semibold hover:bg-alt-blk hover:text-white transition-colors"
                >
                  Official page
                </a>
              )}
            </div>
          </article>
        )}
      </section>
    </Layout>
  );
};

export default FestDetail;
